'use client';

import { type JSX, useState } from 'react';

import { Text } from '@videoblocks/design-system';

import { useChatSessionContext } from '../../_context/SessionContext';
import { useSearchResults } from '../../_hooks';
import { UsageLimitBanner } from '../browse-limit';
import { HelperCardCarousel } from '../helper-cards';
import { AudioResultsGrid } from './AudioResultsGrid';
import { GoToKeywordSearchLink } from './GoToKeywordSearchLink';
import { ResultTypeToggle, type ResultContentType } from './ResultTypeToggle';
import { VideoResultsGrid } from './VideoResultsGrid';
import { VideoResultsGridSkeleton } from './VideoResultsGridSkeleton';

export function ResultsPane(): JSX.Element {
    const { results, totalCount, isSearching, hasSearched } = useSearchResults();
    const { hasReachedLimit, supportsAudio, currentTitle, showTopResults } = useChatSessionContext();
    const [selectedType, setSelectedType] = useState<ResultContentType>('video');

    const audioResults = results.filter((r) => r.contentClass === 'audio');
    const videoResults = results.filter((r) => r.contentClass !== 'audio');
    const showAudio = supportsAudio && audioResults.length > 0;
    const showVideo = videoResults.length > 0;
    const hasBothTypes = showAudio && showVideo;
    const showToggle = supportsAudio;
    const activeType: ResultContentType = hasBothTypes ? selectedType : showAudio ? 'audio' : 'video';

    function renderContent(): JSX.Element {
        if (isSearching) return <VideoResultsGridSkeleton />;
        if (hasSearched && (showAudio || showVideo)) {
            return activeType === 'audio' ? (
                <AudioResultsGrid audioItems={audioResults} />
            ) : (
                <VideoResultsGrid results={videoResults} />
            );
        }
        return <HelperCardCarousel />;
    }

    return (
        <div className="flex flex-col h-full @container">
            <div className="flex flex-col gap-2 bg-white border-b border-gray-200 px-6 py-3 @2xl:h-[--spacing(14.25)] @2xl:flex-row @2xl:items-center @2xl:justify-between @2xl:gap-4 @2xl:py-0">
                <div className="text-left">
                    <Text fontSize="base" fontWeight="semibold" className="leading-tight">
                        {currentTitle}
                    </Text>
                    <Text fontSize="sm" fontWeight="regular" className="text-gray-500">
                        Top {hasSearched ? totalCount : 0} Displayed
                    </Text>
                </div>
                {showToggle && (
                    <ResultTypeToggle
                        value={activeType}
                        onChange={setSelectedType}
                        videoCount={videoResults.length}
                        audioCount={audioResults.length}
                        className="w-full @2xl:w-auto"
                    />
                )}
                {/* DOM order (title → toggle → link) matches the wide layout for keyboard focus
                    order; the narrow stacked layout lifts the link to the top via `order-first`. */}
                <GoToKeywordSearchLink
                    contentClass={activeType}
                    className="order-first self-end @2xl:order-none @2xl:self-auto"
                />
            </div>
            {hasReachedLimit && <UsageLimitBanner />}
            <div className={showTopResults ? 'flex-1 overflow-y-auto bg-gray-100' : 'flex-1 overflow-y-auto'}>
                {renderContent()}
            </div>
        </div>
    );
}
